<template>
  <div :class="rootClasses">
    <template v-if="useAuthenticatedShell">
      <AppLayout>
        <div class="app-shell__view">
          <router-view />
        </div>
      </AppLayout>
    </template>

    <router-view v-else />
    <AppDialogHost />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import AppLayout from '@/layouts/AppLayout.vue'
import AppDialogHost from '@/components/AppDialogHost.vue'
import api from '@/api'
import { cuentaCorrienteClienteNombre } from '@/composables/cuentaCorrienteContext'

export default defineComponent({
  components: {
    AppLayout,
    AppDialogHost
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()
    const hydratingUser = ref(false)
    const systemTheme = ref<'light' | 'dark'>('light')
    let mediaQuery: MediaQueryList | null = null

    const useAuthenticatedShell = computed(() => {
      const logueado = store.getters.isAuthenticated
      return logueado && !Boolean(route.meta.publicPage)
    })

    const currentUser = computed(() => store.getters.currentUser)
    const themePreference = computed(
      () => currentUser.value?.receipt_profile?.theme_preference || 'system'
    )
    const resolvedTheme = computed(() =>
      themePreference.value === 'dark' || themePreference.value === 'light'
        ? themePreference.value
        : systemTheme.value
    )

    const rootClasses = computed(() => ({
      'app-root': true,
      'app-shell': useAuthenticatedShell.value,
      'app-public': !useAuthenticatedShell.value
    }))

    watchEffect(() => {
      const defaultTitle = typeof route.meta.title === 'string' ? route.meta.title : null
      const pageTitle = route.name === 'CuentaCorriente' && cuentaCorrienteClienteNombre.value
        ? `Cuenta corriente - ${cuentaCorrienteClienteNombre.value}`
        : defaultTitle
      document.title = pageTitle ? `${pageTitle} | DocBrain` : 'DocBrain'
    })

    watchEffect(() => {
      document.documentElement.setAttribute('data-bs-theme', resolvedTheme.value)
      document.body.dataset.theme = resolvedTheme.value
    })

    const syncSystemTheme = () => {
      systemTheme.value = mediaQuery?.matches ? 'dark' : 'light'
    }

    const hydrateCurrentUser = async () => {
      if (!store.getters.isAuthenticated || currentUser.value || hydratingUser.value) return

      hydratingUser.value = true
      try {
        const res = await api.get('/auth/me')
        store.commit('setCurrentUser', res.data)
      } catch {
        store.commit('clearAuth')
        if (!route.meta.publicPage) {
          router.push('/login')
        }
      } finally {
        hydratingUser.value = false
      }
    }

    onMounted(() => {
      mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      syncSystemTheme()
      mediaQuery.addEventListener('change', syncSystemTheme)
      hydrateCurrentUser()
    })

    onBeforeUnmount(() => {
      mediaQuery?.removeEventListener('change', syncSystemTheme)
    })

    watch(
      () => store.getters.isAuthenticated,
      (isAuthenticated) => {
        if (isAuthenticated) {
          hydrateCurrentUser()
        }
      },
      { immediate: true }
    )

    return {
      rootClasses,
      useAuthenticatedShell
    }
  }
})
</script>
